import { useMemo } from 'react';
import { Breadcrumb } from 'antd';
import { SettingOutlined, ControlOutlined, ShopOutlined, BankOutlined, TeamOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { generateBreadcrumbTrail, BreadcrumbItem } from '../../config/breadcrumb.config';
import { useBreadcrumbContextSafe } from './BreadcrumbProvider';
import { trpc } from '../../lib/api';

export type LayoutType = 'app' | 'settings' | 'admin' | 'agency' | 'partner' | 'shared-app';

interface AppBreadcrumbProps {
  /** レイアウトタイプ */
  layoutType: LayoutType;
  /** 追加のスタイル */
  style?: React.CSSProperties;
  /** 追加のクラス名 */
  className?: string;
}

/**
 * アプリ全体で使用するパンくずリストコンポーネント
 *
 * 使用例:
 * ```tsx
 * // MainLayout内
 * <AppBreadcrumb layoutType="app" />
 *
 * // SettingsLayout内
 * <AppBreadcrumb layoutType="settings" />
 * ```
 */
export function AppBreadcrumb({ layoutType, style, className }: AppBreadcrumbProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { dynamicTitles } = useBreadcrumbContextSafe();

  // 会社情報を取得（app と shared-app レイアウトのみ）
  const shouldFetchCompany = layoutType === 'app' || layoutType === 'shared-app';
  const { data: company } = trpc.company.getCurrentCompany.useQuery(undefined, {
    enabled: shouldFetchCompany,
    staleTime: 30000, // 30秒間キャッシュ
  });

  // 会社名（取得できない場合はホームを使用）
  const companyName = company?.name || 'ホーム';

  // パンくずリストを生成
  const breadcrumbItems = useMemo(() => {
    const trail = generateBreadcrumbTrail(location.pathname, layoutType);
    return trail.map((item, index) => {
      const isLast = index === trail.length - 1;
      const isHome = index === 0 && (layoutType === 'app' || layoutType === 'shared-app');
      const title = isHome ? companyName : getDisplayTitle(item, dynamicTitles, location.pathname);
      const icon = getIcon(item, index, layoutType);

      return {
        key: item.path,
        title: isLast ? (
          <span>
            {icon}
            {title}
          </span>
        ) : (
          <a
            onClick={(e) => {
              e.preventDefault();
              navigate(item.path);
            }}
            style={{ cursor: 'pointer' }}
          >
            {icon}
            {title}
          </a>
        ),
      };
    });
  }, [location.pathname, layoutType, dynamicTitles, navigate, companyName]);

  // パンくずリストが1つ以下の場合は表示しない
  if (breadcrumbItems.length <= 1) {
    return null;
  }

  return (
    <Breadcrumb
      items={breadcrumbItems}
      style={{
        marginBottom: '16px',
        ...style,
      }}
      className={className}
    />
  );
}

/**
 * 表示するタイトルを取得
 * 動的タイトルが設定されていればそれを使用
 */
function getDisplayTitle(
  item: BreadcrumbItem,
  dynamicTitles: Record<string, string>,
  currentPath: string
): string {
  // 現在のページで、動的タイトルが設定されている場合
  if (item.dynamic && item.path === currentPath) {
    // パスから動的タイトルのキーを推測
    const key = getDynamicTitleKey(currentPath);
    if (key && dynamicTitles[key]) {
      return dynamicTitles[key];
    }
  }
  return item.title;
}

/**
 * パスから動的タイトルのキーを推測
 */
function getDynamicTitleKey(path: string): string | null {
  // 各種詳細ページのパターン
  if (path.match(/\/app\/equipments\/[^/]+$/)) return 'equipment';
  if (path.match(/\/app\/deterioration\/[^/]+$/)) return 'deterioration';
  if (path.match(/\/app\/maintenance\/[^/]+$/)) return 'schedule';
  if (path.match(/\/app\/schedule\/[^/]+$/)) return 'schedule';
  if (path.match(/\/app\/work-order-templates\/[^/]+$/)) return 'work-order-template';
  if (path.match(/\/app\/work-history\/[^/]+$/)) return 'work-history';
  if (path.match(/\/app\/emergency-works\/[^/]+$/)) return 'emergency-work';
  if (path.match(/\/app\/parts\/[^/]+$/)) return 'part';
  if (path.match(/\/app\/suppliers\/[^/]+$/)) return 'supplier';
  if (path.match(/\/app\/purchase-orders\/[^/]+$/)) return 'purchase-order';
  if (path.match(/\/admin\/companies\/[^/]+$/)) return 'company';
  if (path.match(/\/admin\/agencies\/[^/]+$/)) return 'agency';
  if (path.match(/\/admin\/partners\/[^/]+$/)) return 'partner';
  if (path.match(/\/partner\/companies\/[^/]+$/)) return 'partner-company';
  if (path.match(/\/shared-app\/maintenance\/[^/]+$/)) return 'schedule';
  if (path.match(/\/shared-app\/work-history\/[^/]+$/)) return 'work-history';
  if (path.match(/\/shared-app\/emergency-works\/[^/]+$/)) return 'emergency-work';
  return null;
}

/**
 * パンくずリストのアイコンを取得
 */
function getIcon(
  _item: BreadcrumbItem,
  index: number,
  layoutType: LayoutType
): React.ReactNode {
  // 最初の項目のみアイコンを表示
  if (index !== 0) return null;

  switch (layoutType) {
    case 'app':
    case 'shared-app':
      return <BankOutlined style={{ marginRight: 4 }} />;
    case 'settings':
      return <SettingOutlined style={{ marginRight: 4 }} />;
    case 'admin':
      return <ControlOutlined style={{ marginRight: 4 }} />;
    case 'agency':
      return <ShopOutlined style={{ marginRight: 4 }} />;
    case 'partner':
      return <TeamOutlined style={{ marginRight: 4 }} />;
    default:
      return <BankOutlined style={{ marginRight: 4 }} />;
  }
}
